import React, { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";
import { ArrowLeft, Edit2, Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import "./AdmissionFormBuilder.css";
import "./AddAdmission.css";

const emptyPlan = () => ({
    name: "",
    description: "",
    fee: 0,
    features: [],
    status: "Active",
    visible_on_ui: true,
    sort_order: 0,
    center: "",
    batch_id: "",
    duration_label: "",
    badge: "",
});

function PlanEditor({ plan, onSave, onClose, saving }) {
    const [draft, setDraft] = useState(plan);
    const [featureInput, setFeatureInput] = useState("");

    useEffect(() => {
        setDraft(plan);
    }, [plan]);

    const addFeature = () => {
        const f = featureInput.trim();
        if (!f) return;
        setDraft((p) => ({ ...p, features: [...(p.features || []), f] }));
        setFeatureInput("");
    };

    return (
        <div className="apl-editor-overlay" onClick={onClose} role="presentation">
            <div className="apl-editor-modal" onClick={(e) => e.stopPropagation()} role="dialog">
                <h2>{plan.id ? "Edit plan" : "New admission plan"}</h2>

                <label className="afb-label">
                    Plan name *
                    <input
                        className="admission-form-input"
                        value={draft.name}
                        onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))}
                        placeholder="e.g. Regular Course — 3 Months"
                    />
                </label>

                <label className="afb-label">
                    Description
                    <textarea
                        className="admission-form-input"
                        rows={2}
                        value={draft.description || ""}
                        onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))}
                    />
                </label>

                <div className="afb-grid-2">
                    <label className="afb-label">
                        Fee (₹)
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="admission-form-input"
                            value={draft.fee}
                            onChange={(e) => setDraft((p) => ({ ...p, fee: e.target.value }))}
                        />
                    </label>
                    <label className="afb-label">
                        Duration label
                        <input
                            className="admission-form-input"
                            value={draft.duration_label || ""}
                            onChange={(e) => setDraft((p) => ({ ...p, duration_label: e.target.value }))}
                            placeholder="e.g. 3 months"
                        />
                    </label>
                </div>

                <label className="afb-label">
                    Badge (optional)
                    <input
                        className="admission-form-input"
                        value={draft.badge || ""}
                        onChange={(e) => setDraft((p) => ({ ...p, badge: e.target.value }))}
                        placeholder="e.g. Most Popular"
                    />
                </label>

                <label className="afb-label">
                    Default center (optional)
                    <input
                        className="admission-form-input"
                        value={draft.center || ""}
                        onChange={(e) => setDraft((p) => ({ ...p, center: e.target.value }))}
                        placeholder="Pre-select center on form"
                    />
                </label>

                <div>
                    <strong style={{ fontSize: "0.85rem" }}>Features</strong>
                    <div className="apl-feature-input-row" style={{ marginTop: 8 }}>
                        <input
                            className="admission-form-input"
                            value={featureInput}
                            onChange={(e) => setFeatureInput(e.target.value)}
                            placeholder="Add a feature bullet"
                            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                        />
                        <button type="button" className="afb-btn-secondary" onClick={addFeature}>
                            <Plus size={14} />
                        </button>
                    </div>
                    <div className="apl-feature-list" style={{ marginTop: 8 }}>
                        {(draft.features || []).map((f, i) => (
                            <div key={i} className="apl-feature-item">
                                <span>{f}</span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setDraft((p) => ({
                                            ...p,
                                            features: p.features.filter((_, j) => j !== i),
                                        }))
                                    }
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="afb-settings-toggles">
                    <label className="afb-label afb-label--check">
                        <input
                            type="checkbox"
                            checked={draft.status === "Active"}
                            onChange={(e) =>
                                setDraft((p) => ({ ...p, status: e.target.checked ? "Active" : "Inactive" }))
                            }
                        />
                        Plan is active
                    </label>
                    <label className="afb-label afb-label--check">
                        <input
                            type="checkbox"
                            checked={draft.visible_on_ui !== false}
                            onChange={(e) => setDraft((p) => ({ ...p, visible_on_ui: e.target.checked }))}
                        />
                        Visible on public form
                    </label>
                </div>

                <div className="afb-fields-toolbar">
                    <button type="button" className="admission-form-btn admission-form-btn--secondary" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="admission-form-btn admission-form-btn--primary"
                        disabled={saving || !draft.name.trim()}
                        onClick={() => onSave(draft)}
                    >
                        {saving ? "Saving…" : "Save plan"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const AdmissionPlans = () => {
    const url = API_BASE_URL;
    const token = decryptToken("accessToken") || "";
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editorPlan, setEditorPlan] = useState(null);
    const [saving, setSaving] = useState(false);

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
        [token],
    );

    const loadPlans = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${url}/admission/form/plans`, { headers: authHeaders });
            if (!res.ok) throw new Error("Failed");
            const data = await res.json();
            setPlans(data.plans || []);
        } catch {
            toast.error("Could not load plans");
        } finally {
            setLoading(false);
        }
    }, [url, authHeaders]);

    useEffect(() => {
        loadPlans();
    }, [loadPlans]);

    const savePlan = async (draft) => {
        setSaving(true);
        try {
            const isNew = !draft.id;
            const endpoint = isNew
                ? `${url}/admission/form/plans`
                : `${url}/admission/form/plans/${draft.id}`;
            const res = await fetch(endpoint, {
                method: isNew ? "POST" : "PUT",
                headers: authHeaders,
                body: JSON.stringify({
                    ...draft,
                    fee: Number(draft.fee) || 0,
                    batch_id: draft.batch_id || null,
                    center: draft.center || null,
                }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || "Save failed");
            }
            toast.success(isNew ? "Plan created" : "Plan updated");
            setEditorPlan(null);
            loadPlans();
        } catch (err) {
            toast.error(err.message || "Could not save plan");
        } finally {
            setSaving(false);
        }
    };

    const deletePlan = async (id) => {
        if (!window.confirm("Delete this plan?")) return;
        try {
            const res = await fetch(`${url}/admission/form/plans/${id}`, {
                method: "DELETE",
                headers: authHeaders,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message || "Delete failed");
            toast.success("Plan deleted");
            loadPlans();
        } catch (err) {
            toast.error(err.message || "Could not delete plan");
        }
    };

    const toggleVisibility = async (plan) => {
        try {
            const res = await fetch(`${url}/admission/form/plans/${plan.id}`, {
                method: "PUT",
                headers: authHeaders,
                body: JSON.stringify({ visible_on_ui: !plan.visible_on_ui }),
            });
            if (!res.ok) throw new Error("Update failed");
            loadPlans();
        } catch {
            toast.error("Could not update visibility");
        }
    };

    return (
        <div className="admission-form-shell apl-shell">
            <div className="admission-form-container afb-container">
                <div className="admission-form-header">
                    <Link to="/admission-form-builder" className="admission-form-back">
                        <ArrowLeft size={18} /> Form builder
                    </Link>
                    <div className="admission-form-header-text">
                        <h1>Admission Plans</h1>
                        <p>Create pricing plans shown on the public admission form. Only active, visible plans appear to applicants.</p>
                    </div>
                    <button
                        type="button"
                        className="admission-form-btn admission-form-btn--primary"
                        onClick={() => setEditorPlan(emptyPlan())}
                    >
                        <Plus size={16} /> New plan
                    </button>
                </div>

                {loading ? (
                    <p className="afb-loading">Loading plans…</p>
                ) : plans.length === 0 ? (
                    <div className="admission-form-card" style={{ padding: 32, textAlign: "center" }}>
                        <p style={{ color: "var(--af-muted)", marginBottom: 16 }}>No plans yet. Create your first admission plan.</p>
                        <button
                            type="button"
                            className="admission-form-btn admission-form-btn--primary"
                            onClick={() => setEditorPlan(emptyPlan())}
                        >
                            <Plus size={16} /> Create plan
                        </button>
                    </div>
                ) : (
                    <div className="apl-plans-grid">
                        {plans.map((plan) => (
                            <div
                                key={plan.id}
                                className={`apl-plan-card ${plan.status === "Inactive" ? "apl-plan-card--inactive" : ""}`}
                            >
                                <div className="apl-plan-card__head">
                                    <div>
                                        <h3 style={{ margin: "0 0 4px" }}>{plan.name}</h3>
                                        {plan.duration_label && (
                                            <span style={{ fontSize: "0.85rem", color: "var(--af-muted)" }}>
                                                {plan.duration_label}
                                            </span>
                                        )}
                                    </div>
                                    {plan.badge && <span className="apl-plan-card__badge">{plan.badge}</span>}
                                </div>

                                <div className="apl-plan-card__fee">
                                    ₹{Number(plan.fee).toLocaleString("en-IN")}
                                </div>

                                {plan.description && (
                                    <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--af-muted)" }}>
                                        {plan.description}
                                    </p>
                                )}

                                {(plan.features || []).length > 0 && (
                                    <ul className="apl-features">
                                        {plan.features.map((f, i) => (
                                            <li key={i}>{f}</li>
                                        ))}
                                    </ul>
                                )}

                                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    <span className={`apl-status-badge apl-status-badge--${plan.status === "Active" ? "active" : "inactive"}`}>
                                        {plan.status}
                                    </span>
                                    <span className="apl-visible-badge">
                                        {plan.visible_on_ui ? "Visible on UI" : "Hidden from UI"}
                                    </span>
                                </div>

                                <div className="apl-plan-actions">
                                    <button
                                        type="button"
                                        className="afb-btn-secondary"
                                        onClick={() => setEditorPlan({ ...plan, features: plan.features || [] })}
                                    >
                                        <Edit2 size={14} /> Edit
                                    </button>
                                    <button type="button" className="afb-btn-secondary" onClick={() => toggleVisibility(plan)}>
                                        {plan.visible_on_ui ? <EyeOff size={14} /> : <Eye size={14} />}
                                        {plan.visible_on_ui ? "Hide" : "Show"}
                                    </button>
                                    <button
                                        type="button"
                                        className="afb-btn-secondary afb-btn--danger"
                                        onClick={() => deletePlan(plan.id)}
                                    >
                                        <Trash2 size={14} /> Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {editorPlan && (
                    <PlanEditor
                        plan={editorPlan}
                        onSave={savePlan}
                        onClose={() => setEditorPlan(null)}
                        saving={saving}
                    />
                )}
            </div>
        </div>
    );
};

export default AdmissionPlans;
